import mapiConfig from "../configs/mapi";
import * as Sentry from "@sentry/browser";
import { logError } from "./utils/logService";
import { httpMStepsClient } from "./utils/httpService";
import { adminHttpMStepsClient } from "./utils/adminHttpService";
import { oktaBypassUserClaimInformation } from "../models/usersModel";

const BASE_URL = mapiConfig.api.domain;

export const pushFileToS3 = async (
  httpMethod: string,
  fileBase64: any,
  name: string,
  contentType: string,
  module?: string
) => {
  let methodPath = "admin/saveS3File";

  if (module) {
    methodPath += `?module=${module}`;
  }

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: httpMethod,
    path: methodPath,
    body: {
      file: fileBase64,
      fileName: name,
      contentType: contentType,
    },
    isAcceptHeaderRequired: false,
  })
    .then((result) => {
      if (result.data) {
        return {
          signedUrl: result.data.fileUrl,
          isLoading: false,
        };
      } else {
        return {
          signedUrl: null,
          isLoading: false,
        };
      }
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });
};

export const getFileFromS3 = async (fileName: string, module?: string) => {
  const methodPath = "admin/getImageFromS3";
  let finalFileName = fileName;
  if (module) {
    finalFileName = module + "/" + fileName;
  }
  const _httpBody = {
    fileName: finalFileName,
  };

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "POST",
    path: methodPath,
    body: _httpBody,
  })
    .then((result) => {
      return result.data;
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });
};

export async function adminUserLogin(requestBody: any) {
  const pathURL = BASE_URL.endsWith("/") ? `admin/login` : `/admin/login`;
  return await httpMStepsClient({
    baseURL: BASE_URL,
    type: "POST",
    path: pathURL,
    body: requestBody,
    isTokenRequired: false,
  });
}

export async function updateAdminUserAccount(userId: string, requestBody: any) {
  const pathURL = BASE_URL.endsWith("/")
    ? `admin/users/${userId}`
    : `/admin/users/${userId}`;
  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "PATCH",
    path: pathURL,
    body: requestBody,
    isTokenRequired: false,
  });
}

export const getOrganizationalUsers = async () => {
  const methodPath = `admin/organizational-users`;

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  })
    .then((result) => {
      return {
        users: result.data,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);

      return {
        users: [],
        isLoading: false,
      };
    });
};

export const searchOrganizationalUsers = async (search: string) => {
  const methodPath = `admin/organizational-users?search=${search}`;

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  })
    .then((result) => {
      return {
        users: result.data,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);

      return {
        users: [],
        isLoading: false,
      };
    });
};

export const getOrganizationalUserById = async (userId: string) => {
  const methodPath = `admin/organizational-users/${userId}`;

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  })
    .then((result) => {
      return result.data;
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
      return error;
    });
};

export const deleteOrganizationalUserById = async (userId: string) => {
  const methodPath = `admin/organizational-users/${userId}`;
  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "DELETE",
    path: methodPath,
  })
    .then((result) => {
      return {
        data: result.data,
        isSuccessfull: true,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);

      return {
        isSuccessfull: false,
        isLoading: false,
      };
    });
};

export const updateOrganizationalUser = async (
  requestBody: any,
  userId: string
) => {
  const methodPath = `admin/organizational-users/${userId}`;
  const httpBody: oktaBypassUserClaimInformation = {
    login: requestBody.login,
    email: requestBody.email as string,
    auto_sync: false,
    company_ids: requestBody.company_ids,
    first_name: requestBody.firstName,
    last_name: requestBody.lastName,
    display_name: requestBody.displayName,
    zone_info: requestBody.zone,
    team: requestBody.team,
    password: requestBody.password,
    roles: requestBody.roles,
  };

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "PATCH",
    path: methodPath,
    body: httpBody,
  })
    .then((response) => {
      return {
        status: "ACTIVE",
        users: httpBody,
        isLoading: false,
        response: response,
      };
    })
    .catch((error) => {
      // Check if error is defined
      if (error) {
        logError(error);
        Sentry.captureException(error);
        return {
          status: "FAILED",
          isLoading: false,
          error: error,
        };
      } else {
        console.log("Caught error: Undefined error");
        logError("Undefined error caught");
        return {
          status: "FAILED",
          isLoading: false,
          error: "An unexpected error occurred",
        };
      }
    });
};

export const createOrganizationalUser = async (requestBody: any) => {
  const methodPath = `admin/organizational-users`;
  const httpBody: oktaBypassUserClaimInformation = {
    login: requestBody.login,
    email: requestBody.email as string,
    auto_sync: false,
    company_ids: requestBody.company_ids,
    first_name: requestBody.firstName,
    last_name: requestBody.lastName,
    display_name: requestBody.displayName,
    zone_info: requestBody.zone,
    team: requestBody.team,
    password: requestBody.password,
    roles: requestBody.roles,
  };

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "POST",
    path: methodPath,
    body: httpBody,
  })
    .then((result) => {
      return {
        data: result.data,
        isSuccessfull: true,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);

      return {
        isSuccessfull: false,
        isLoading: false,
        data: error,
      };
    });
};

//  ORGANIZATIONS APIs
export const getAllOrganizationsList = async () => {
  const methodPath = `admin/organizations`;

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  })
    .then((result) => {
      return {
        company: result.data,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });
};

export const createOrganization = async (requestBody: any) => {
  const methodPath = `admin/organizations`;

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "POST",
    path: methodPath,
    body: requestBody,
  })
    .then((result) => {
      return {
        company: result.data,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });
};

export const updateOrganization = async (
  requestBody: any,
  organizationId: string
) => {
  const methodPath = `admin/organizations/${organizationId}`;

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "PUT",
    path: methodPath,
    body: requestBody,
  })
    .then((result) => {
      return {
        company: result.data,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });
};

export const getOrganizationsById = async (companyId: string) => {
  const methodPath = `admin/organizations/${companyId}`;

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  })
    .then((result) => {
      return result.data;
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
      return error;
    });
};

export const deleteOrganizationsById = async (companyId: string) => {
  const methodPath = `admin/organizations/${companyId}`;
  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "DELETE",
    path: methodPath,
  })
    .then((result) => {
      return {
        data: result.data,
        isSuccessfull: true,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);

      return {
        isSuccessfull: false,
        isLoading: false,
      };
    });
};

export const searchOrganizations = async (search: string) => {
  const methodPath = `admin/organizations?search=${search}`;

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  })
    .then((result) => {
      return {
        company: result.data,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });
};

export const createAdminOrganizations = async (body: any) => {
  const methodPath = `admin/organizations`;

  return await adminHttpMStepsClient({
    baseURL: BASE_URL,
    type: "POST",
    path: methodPath,
    body: body,
  });
};
